import React from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { PopoverTrigger } from '@mentimeter/ragnar-ui/popover';
import { Button, type ButtonT } from '@mentimeter/ragnar-ui/button';
import { Tooltip } from '@mentimeter/ragnar-ui/tooltip';
import { blackBase, shade } from '@mentimeter/ragnar-colors';
import { useDevice } from '@mentimeter/ragnar-device';
import { PlusIcon } from '@mentimeter/ragnar-visuals';
import { IconButton } from '@mentimeter/ragnar-ui/icon-button';
import type { IconButtonProps } from '@mentimeter/ragnar-ui/icon-button';
import type { Extend } from '@mentimeter/ragnar-dsc';

export const ADD_SLIDE_BUTTON_ID = 'add-slide-button';

const RotatingIcon = ({
  shouldRotate,
  showIconButton,
}: { shouldRotate: boolean; showIconButton: boolean }) => {
  return (
    <Box
      mr={showIconButton ? 0 : 1}
      aria-hidden={true}
      extend={({ theme }) => ({
        transition: `transform ${theme.durations[1]}s ease`,
        transform: shouldRotate ? 'rotate(45deg)' : 'rotate(0)',
      })}
    >
      <PlusIcon size={2} color="inherit" />
    </Box>
  );
};

export const AddSlideButtonWithTooltip = ({
  onClickAddSlideButton,
  showAddSlidePopover,
  disabled,
  description,
  showIconButton = false,
}: {
  onClickAddSlideButton?: () => void;
  showAddSlidePopover: boolean;
  disabled: boolean;
  description?: string;
  showIconButton?: boolean;
}) => {
  const { breakpoint } = useDevice();
  const buttonSize = breakpoint > 3 ? 'default' : 'compact';

  const buttonExtend: Extend = ({ theme }) =>
    showAddSlidePopover
      ? {
          background: shade(theme.colors.primary, blackBase, 0.3),
          borderColor: shade(theme.colors.primary, blackBase, 0.3),
          '&:hover': {
            background: shade(theme.colors.primary, blackBase, 0.3),
            borderColor: shade(theme.colors.primary, blackBase, 0.3),
          },
        }
      : {};

  const buttonProps: Partial<ButtonT & IconButtonProps> = showIconButton
    ? {
        width: '25px',
        py: '28px',
        px: 'space0',
      }
    : {};

  const boxProps = showIconButton
    ? {
        pr: 'space1',
        pt: '16.8px',
      }
    : {
        width: '100%',
        p: 'space4',
        pl: 'space8',
      };

  const ButtonComponent = showIconButton ? IconButton : Button;
  const buttonContent = (
    <ButtonComponent
      {...buttonProps}
      size={buttonSize}
      disabled={disabled}
      variant="primary"
      onClick={onClickAddSlideButton}
      id={ADD_SLIDE_BUTTON_ID}
      data-testid={
        showAddSlidePopover ? 'cancel-add-slide-btn' : 'add-slide-btn'
      }
      extend={buttonExtend}
    >
      <RotatingIcon
        shouldRotate={showAddSlidePopover}
        showIconButton={showIconButton}
      />
      {showIconButton ? '' : 'New slide'}
    </ButtonComponent>
  );

  return (
    <Tooltip
      data-testid="disabled-add-slide-tooltip"
      side="bottom"
      trigger={
        <Box
          alignItems="stretch"
          gap={2}
          id="add-slide-wrapper"
          data-testid="add-slide-wrapper"
          {...boxProps}
        >
          {!disabled ? (
            <PopoverTrigger>{buttonContent}</PopoverTrigger>
          ) : (
            buttonContent
          )}
        </Box>
      }
    >
      {description || ''}
    </Tooltip>
  );
};
